import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../api/axios';
import AuthContext from '../context/AuthContext';
import { FaCheckCircle, FaTimesCircle, FaQuestionCircle, FaEye, FaEyeSlash } from 'react-icons/fa'; 
import './TestExecution.css';

// Importiamo il nuovo componente (Assicurati che il percorso sia giusto)
import ProctoringMonitor from './ProctoringMonitor'; 

// --- CONFIGURAZIONE FUNZIONALITÀ ---
const ENABLE_PROCTORING_FEATURE = true; // Imposta a FALSE per disattivare completamente il codice relativo da UI

const TestExecution = () => {
    const { testId } = useParams();
    const { auth } = useContext(AuthContext);
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [testInfo, setTestInfo] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [answers, setAnswers] = useState({});
    const [timeLeft, setTimeLeft] = useState(0);
    const [submitting, setSubmitting] = useState(false);

    // --- NUOVI STATI PER PROCTORING ---
    const [isProctoringActive, setIsProctoringActive] = useState(false);
    const [alertCount, setAlertCount] = useState(0);

    const [modalState, setModalState] = useState({
        type: 'none', 
        title: '',
        message: '',
        score: null
    });

    // ... [Il tuo useEffect di caricamento test rimane UGUALE] ...
    useEffect(() => {
        if (!auth || !auth.user_id) return;
        const fetchTest = async () => {
            try {
                const res = await axios.get(`/api_start_test.php?test_id=${testId}&user_id=${auth.user_id}`);
                if (res.data.success) {
                    setTestInfo(res.data.data.test_info);
                    setQuestions(res.data.data.questions);
                    const duration = parseInt(res.data.data.test_info.duration_minutes) || 30;
                    setTimeLeft(duration * 60);
                    setLoading(false);
                } else {
                    showModal('error', 'Errore Caricamento', res.data.message);
                }
            } catch (err) {
                console.error(err);
                showModal('error', 'Errore Connessione', 'Impossibile caricare il test.');
            }
        };
        fetchTest();
    }, [testId, auth, navigate]);

    // ... [Il tuo useEffect Timer rimane UGUALE] ...
    useEffect(() => {
        if (!loading && timeLeft > 0 && modalState.type === 'none') {
            const timerId = setInterval(() => setTimeLeft(t => t - 1), 1000);
            return () => clearInterval(timerId);
        }
    }, [timeLeft, loading, modalState.type]);

    const showModal = (type, title, message, score = null) => {
        setModalState({ type, title, message, score });
    };

    const handleSelectOption = (questionId, answerId) => {
        setAnswers(prev => ({ ...prev, [questionId]: answerId }));
    };

    const handleFinishClick = () => {
        const answeredCount = Object.keys(answers).length;
        const totalCount = questions.length;
        
        // Se il proctoring è attivo, aggiungiamo il report al messaggio
        let extraMsg = "";
        if (isProctoringActive) {
            extraMsg = `\n\n⚠️ Report Sorveglianza: Rilevate ${alertCount} infrazioni.`;
        }

        showModal(
            'confirm', 
            'Confermi l\'invio?', 
            `Hai risposto a ${answeredCount} su ${totalCount} domande.${extraMsg}\nUna volta inviato non potrai modificare le risposte.`
        );
    };

    const confirmSubmit = async () => {
        setSubmitting(true);
        // Disattiva telecamera all'invio
        setIsProctoringActive(false);

        try {
            const payload = {
                test_id: testId,
                user_id: auth.user_id,
                // Inviamo anche i dati di proctoring al backend
                proctoring_data: {
                    active: isProctoringActive,
                    alert_count: alertCount
                },
                answers: questions.map(q => ({
                    question_id: q.id,
                    answer_id: answers[q.id] || null
                }))
            };

            const res = await axios.post('/api_submit_test.php', payload);
            
            if (res.data.success) {
                showModal('success', 'Test Completato!', 'Le tue risposte sono state salvate.', res.data.score);
            } else {
                showModal('error', 'Errore Salvataggio', res.data.message);
            }
        } catch (err) {
            console.error(err);
            showModal('error', 'Errore di Rete', 'Controlla la tua connessione e riprova.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleCloseModal = () => {
        if (modalState.type === 'success') {
            navigate('/dashboard'); 
        } else {
            setModalState({ type: 'none', title: '', message: '', score: null }); 
        }
    };

    const formatTime = (s) => `${Math.floor(s/60)}:${(s%60).toString().padStart(2,'0')}`;

    // --- FUNZIONE CALLBACK PROCTORING ---
    const handleProctoringAlert = (reason) => {
        // Incrementa contatore
        setAlertCount(prev => prev + 1);
        // Opzionale: Puoi mostrare un Toast o cambiare colore al bordo dello schermo
        console.warn(`Proctoring Alert: ${reason}`);
    };

    // RENDER
    if (!auth || !auth.user_id) return <div className="test-loader">Autenticazione...</div>;
    if (loading) return <div className="test-loader"><div className="spinner"></div>Caricamento Test...</div>;

    return (
        <div className="test-page">
            {/* Header */}
            <div className="test-top-bar">
                <div className="test-info">
                    <h5 className="test-title">{testInfo.name}</h5>
                    <div className="d-flex gap-3 align-items-center">
                        <span className="q-count">{Object.keys(answers).length}/{questions.length} Risposte</span>
                        
                        {/* Indicatore Alert Proctoring */}
                        {isProctoringActive && (
                            <span className="badge bg-danger">
                                <FaEye className="me-1"/> Alert: {alertCount}
                            </span>
                        )}
                    </div>
                </div>
                
                <div className="d-flex gap-2 align-items-center">
                    {/* BOTTONE SIMULA ESAME */}
                    {ENABLE_PROCTORING_FEATURE && !isProctoringActive && (
                        <button 
                            className="btn btn-warning btn-sm fw-bold" 
                            onClick={() => setIsProctoringActive(true)}
                        >
                            <FaEye className="me-2"/> SIMULA ESAME
                        </button>
                    )}
                    
                    {/* Se attivo, bottone per disattivare (opzionale per debug) */}
                    {ENABLE_PROCTORING_FEATURE && isProctoringActive && (
                         <button 
                            className="btn btn-outline-light btn-sm" 
                            onClick={() => setIsProctoringActive(false)}
                            title="Disattiva Sorveglianza"
                        >
                            <FaEyeSlash />
                        </button>
                    )}

                    <div className={`timer-badge ${timeLeft < 60 ? 'urgent' : ''}`}>
                        {formatTime(timeLeft)}
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="test-content">
                {questions.map((q, index) => (
                    <div key={q.id} className="question-card-vertical">
                        <div className="q-header">
                            <span className="q-badge">{index + 1}</span>
                            <span className="q-text">{q.question_text}</span>
                        </div>
                        <div className="q-options">
                            {q.options && q.options.map(opt => {
                                const isSelected = answers[q.id] === opt.id;
                                return (
                                    <div 
                                        key={opt.id} 
                                        className={`option-row ${isSelected ? 'active' : ''}`}
                                        onClick={() => handleSelectOption(q.id, opt.id)}
                                    >
                                        <div className="radio-indicator"></div>
                                        <div className="opt-text">{opt.answer_text}</div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}

                <div className="test-actions">
                    <button className="btn-finish" onClick={handleFinishClick}>
                        TERMINA E INVIA
                    </button>
                </div>
            </div>

            {/* --- COMPONENTE PROCTORING --- */}
            {ENABLE_PROCTORING_FEATURE && (
                <ProctoringMonitor 
                    isActive={isProctoringActive} 
                    onAlert={handleProctoringAlert} 
                />
            )}

            {/* --- MODALE UNIFICATA (Invariata) --- */}
            {modalState.type !== 'none' && (
                <div className="overlay">
                    <div className="modal-box">
                        <div className="modal-icon">
                            {modalState.type === 'confirm' && <FaQuestionCircle className="icon-confirm" />}
                            {modalState.type === 'success' && <FaCheckCircle className="icon-success" />}
                            {modalState.type === 'error' && <FaTimesCircle className="icon-error" />}
                        </div>
                        <h3 className="modal-title">{modalState.title}</h3>
                        {modalState.type === 'success' && modalState.score !== null && (
                            <div className="score-result">Punteggio: {modalState.score}</div>
                        )}
                        <p className="modal-desc" style={{whiteSpace: 'pre-line'}}>{modalState.message}</p>
                        <div className="modal-actions">
                            {modalState.type === 'confirm' ? (
                                <>
                                    <button className="btn-modal btn-secondary" onClick={handleCloseModal} disabled={submitting}>Annulla</button>
                                    <button className="btn-modal btn-primary" onClick={confirmSubmit} disabled={submitting}>
                                        {submitting ? 'Invio...' : 'Conferma'}
                                    </button>
                                </>
                            ) : (
                                <button className={`btn-modal ${modalState.type === 'success' ? 'btn-success' : 'btn-danger'}`} onClick={handleCloseModal}>
                                    {modalState.type === 'success' ? 'Torna alla Dashboard' : 'Chiudi'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TestExecution;